/** Extends the built in String class with static formatting methods
    @see https://stackoverflow.com/questions/30257915/extend-a-string-class-in-es6
    @see http://www.loganfranken.com/blog/864/es6-everyday-extending-built-in-classes/
    @description Caution, there be wolves!
    @see http://kangax.github.io/compat-table/es6/
*/
export default class STRING extends String {
    /** Returns string as camelcase
        @static
        @see https://stackoverflow.com/questions/2970525/converting-any-string-into-camel-case
        @returns {string} A camel case formatted string
    */
    camelcase() {
        return this.split(' ').map(function (word, index) {
            return index === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        }).join('');
    }
    /** Returns string with first character as Capital letter
        @static
        @returns {string} A capitilized string
    */
    capitilize() {
        return this.charAt(0).toUpperCase() + this.slice(1);
    }
    /** Returns only alphanumeric values
        @see https://stackoverflow.com/questions/2970525/converting-any-string-into-camel-case
        @returns {string} A string of only alphanumeric characters from given string
    */
    alphanumeric() {
        return this.replace(/^[a-zA-Z0-9-_]+$/u, '').replace(/[[\]']/gu, '') || '';
    }
    /** Escapes non-friendly characters in HTML strings
        @method
        @returns {ThisType} Character encoded HTML string
    */
    escapeHtml() {
        return this.replace(/&/gu, '&amp;')
            .replace(/</gu, '&lt;')
            .replace(/>/gu, '&gt;')
            .replace(/'/gu, '&quot;')
            .replace(/'/gu, '&#39;');
    }
    /** Returns a friendly formatted string, safe for html and web components
        @method
        @returns {ThisType} HTML friendly string
    */
    friendly() {
        return this.replace(/^[a-z|A-Z|0-9|\s]*/u, '') || '';
    }
    /** Trims the given string to the specified length and applies the given ending
        @param {number} length Length to trim string
        @param {string} ending String to append
        @returns {string} A string truncated to the given length
    */
    truncate(length = 100, ending = '...') {
        return this.length > length ? this.valueOf().substring(0, length - ending.length) + ending : this.valueOf(); //.valueOf()
    }
    /** Trims whitespace from both ends of the string
        @returns {string} A trimmed string
    */
    trim() {
        return this.replace(/^\s+|\s+$/gu, '');
    }
    /** Pad with zeroes
        @param {number} num The original number to be padded
        @param {number} size The number of zeros to pad with
        @returns {string} A zero padded string
    */
    pad(num, size) {
        let s = String(num);
        while (s.length < size) {
            s = '0' + s;
        }
        return s;
    }
    /** Returns a Javascript Date object based on a given .NET JavaScriptSerializer date value
        @see https://stackoverflow.com/a/50292370/722785
        param {string} dateString from .NET Serializer ie: Date(1534759609990)
        @returns {Date} Javascript Date Object
    */
    getDateValue() {
        return new Date(parseInt(this.replace(/\D+/gu, '')));
    }
    /** Create a globally unique identifier
        @returns {string} Globally unique identifier
    */
    guid() {
        const s4 = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1);
        return `${s4() + s4()}-${s4()}-${s4()}-${s4()}-${s4() + s4() + s4()}`;
    }
    /** Tests for a palindrome
        @returns {boolean} True if palindrome
    */
    isPalindrome() {
        if (this.length > 0) {
            return this.toLowerCase() === this.toLowerCase().split('').reverse().join('');
        }
        return false;
    }
    /** Returns up to 5 paragraphs of Lorem Ipsum text
        @param {number} paragraphs Paragraph Count
        @returns {string} Lorem Ipsum Text
    */
    lipsum(paragraphs = 1) {
        let p = [
            '<p>Lorem ipsum dolor sit amet, consectetur adipiscing elit.Nunc vel massa vitae metus eleifend rhoncus.Sed suscipit felis augue, ac eleifend diam porta id.Morbi placerat facilisis congue.Aliquam tincidunt bibendum ultricies.Nam aliquam euismod ipsum id euismod.Donec efficitur urna ac efficitur lacinia.Mauris sit amet enim a mi sollicitudin ultricies.</p>',
            '<p>Nullam sagittis euismod facilisis.Integer eget velit sed mi porttitor euismod.Sed arcu orci, dapibus ac ante non, consequat vulputate eros.Quisque gravida commodo sapien et fermentum.Phasellus hendrerit tincidunt ipsum, ac semper dui pretium vitae.Maecenas ex sapien, dignissim malesuada volutpat non, lacinia sit amet ex.Cras dapibus, nibh ultricies tincidunt posuere, leo libero euismod neque, sed tempus dui enim et ante.</p>',
            '<p>Nullam ac consectetur sem.Nam accumsan ut eros commodo maximus.Aenean eget purus vel diam faucibus luctus.Nam vel aliquam lorem.Etiam dictum orci vitae nunc gravida mollis.Etiam consectetur interdum dictum.In justo ipsum, pretium non lectus sed, malesuada tincidunt felis.Maecenas sit amet tincidunt mi.Curabitur ornare in ex in egestas.Pellentesque vitae diam ut mi scelerisque accumsan nec ac orci.</p>',
            '<p>Curabitur scelerisque sapien et ipsum dapibus euismod.Morbi commodo est sed massa dignissim fringilla.Nunc vel mi vitae nunc placerat sagittis vel at leo.Sed orci orci, vestibulum ut erat interdum, ornare scelerisque diam.Integer tincidunt bibendum erat vitae eleifend.Quisque efficitur ipsum in nulla rutrum, sed vestibulum nunc bibendum.Ut mollis, sem non lacinia imperdiet, sapien odio aliquet nisl, vitae suscipit metus ligula et diam.Nulla fringilla placerat efficitur.Quisque vitae ultrices massa, eu aliquet libero.Vestibulum ante ipsum primis in faucibus orci luctus et ultrices posuere cubilia curae; Curabitur et auctor justo.</p>',
            '<p>Etiam dignissim, libero vel vulputate mollis, tellus lacus vulputate neque, in maximus odio lorem ut lacus.Aliquam fermentum odio eu ante congue convallis.Suspendisse potenti.Phasellus et rutrum tellus.Donec eget egestas tellus.Donec non porta lectus.Aenean et erat a dui accumsan congue.Sed nisl nisl, viverra id lobortis a, placerat ullamcorper tortor.Donec a iaculis purus.Maecenas dapibus aliquam turpis at sagittis.Donec et sagittis nisi.Vestibulum id pulvinar nunc.Mauris ac nunc quis orci euismod fringilla ut ut justo.Nulla facilisi.Proin maximus sagittis condimentum.</p>'
        ];
        return p.slice(0, paragraphs).join('');
    }
}
